<template>
  <div>
    <v-container grid-list-md>
      <!-- HEADER -->
      <v-layout row wrap>
        <v-flex xs12>
          <h1>{{ $t('security_pending_park_contracts') }}</h1>
          <p v-if="parkName">{{ $t('security_pending_park_contracts_description') }} - <strong>{{ parkName }}</strong></p>
          <p v-else>{{ $t('security_pending_park_contracts_description') }}</p>
        </v-flex>
      </v-layout>

      <!-- LOADING STATE -->
      <v-layout row wrap v-if="isLoading">
        <v-flex xs12>
          <v-progress-linear indeterminate></v-progress-linear>
        </v-flex>
      </v-layout>

      <!-- ERROR STATE -->
      <v-layout row wrap v-if="error">
        <v-flex xs12>
          <v-alert type="error" dismissible>
            {{ error }}
          </v-alert>
        </v-flex>
      </v-layout>

      <!-- CONTRACTS LIST -->
      <v-layout row wrap v-if="pendingContracts.length > 0 && !isLoading">
        <v-flex xs12>
          <h2 v-if="pendingContracts.some(c => c.status === 'PENDING')">
            {{ pendingContracts.filter(c => c.status === 'PENDING').length }} {{ $t('security_requests_pending') }}
          </h2>
          <h2 v-else-if="pendingContracts.some(c => c.status === 'ACTIVE')" class="success--text">
            <v-icon color="success" class="mr-2">check_circle</v-icon>
            {{ $t('security_contract_active') }}
          </h2>
        </v-flex>

        <v-flex xs12 sm6 md4 v-for="contract in pendingContracts" :key="contract.id">
          <v-card class="elevation-3">
            <v-card-title primary-title>
              <div>
                <h3 class="headline mb-0">{{ contract.security_company_name }}</h3>
                <small>{{ $t('security_linking_request_label') }}</small>
              </div>
            </v-card-title>
            <v-card-text>
              <v-list dense>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('park_name') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ contract.park_name }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>

                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('security_company_id') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ contract.security_company }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>

                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('status') }}</v-list-tile-title>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    <v-chip :color="getStatusColor(contract.status)" text-color="white" small>
                      {{ contract.status_display || contract.status }}
                    </v-chip>
                  </v-list-tile-action>
                </v-list-tile>

                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('requested_at') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ formatDate(contract.created_at) }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>

              <v-alert v-if="contract.status === 'PENDING'" type="info" class="mt-3">
                {{ $t('security_review_and_respond') }}
              </v-alert>
              <v-alert v-else-if="contract.status === 'ACTIVE'" type="success" class="mt-3">
                {{ $t('security_contract_active') }}
              </v-alert>
              <v-alert v-else-if="contract.status === 'REJECTED'" type="error" class="mt-3">
                {{ $t('security_contract_rejected') }}
              </v-alert>
            </v-card-text>

            <v-card-actions v-if="contract.status === 'PENDING'">
              <v-spacer></v-spacer>
              <v-btn color="error" flat @click="rejectContract(contract)" :loading="loadingContractId === contract.id">
                {{ $t('security_reject') }}
              </v-btn>
              <v-btn color="success" flat @click="approveContract(contract)" :loading="loadingContractId === contract.id">
                {{ $t('security_approve') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- EMPTY STATE -->
      <v-layout row wrap v-if="pendingContracts.length === 0 && !isLoading && !error">
        <v-flex xs12>
          <v-card>
            <v-card-text class="text-center py-5">
              <v-icon size="64" color="success" class="mb-3">check_circle</v-icon>
              <h3>{{ $t('security_no_pending_contracts') }}</h3>
              <p class="mt-2 grey--text">{{ $t('security_no_contracts_message') }}</p>
              <p class="grey--text text-xs">{{ $t('security_no_contracts_description') }}</p>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- CONFIRMATION DIALOG -->
      <v-dialog v-model="showConfirmDialog" max-width="400px">
        <v-card>
          <v-card-title>{{ confirmTitle }}</v-card-title>
          <v-card-text>
            {{ confirmMessage }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" flat @click="showConfirmDialog = false">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn :color="confirmAction === 'APPROVE' ? 'success' : 'error'" 
                   flat 
                   @click="confirmAction === 'APPROVE' ? submitApprove() : submitReject()"
                   :loading="isLoading">
              {{ $t(confirmAction === 'APPROVE' ? 'security_approve' : 'security_reject') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { RESPOND_TO_PARK_CONTRACT } from '../../../store/modules/security'
import { USER_REQUEST } from '../../../store/actions/user'
import { mapGetters } from 'vuex'
import axios from 'axios'

const API_BASE_URL = 'https://empresa-service.intrare.app'

export default {
  name: 'PendingParkContracts',
  data() {
    return {
      pendingContracts: [],
      isLoading: false,
      error: null,
      loadingContractId: null,
      showConfirmDialog: false,
      confirmAction: null,
      selectedContract: null,
      parkId: null,
      parkName: null,
      totalContracts: 0
    }
  },
  computed: {
    ...mapGetters(['getToken']),
    confirmTitle() {
      return this.confirmAction === 'APPROVE' 
        ? this.$t('security_confirm_approve')
        : this.$t('security_confirm_reject')
    },
    confirmMessage() {
      if (!this.selectedContract) return ''
      if (this.confirmAction === 'APPROVE') {
        return `¿Aprobar la vinculación de ${this.selectedContract.security_company_name}?`
      } else {
        return `¿Rechazar la solicitud de ${this.selectedContract.security_company_name}?`
      }
    }
  },
  methods: {
    getStatusColor(status) {
      const statusColors = {
        'ACTIVE': 'success',
        'PENDING': 'warning',
        'REJECTED': 'error'
      }
      return statusColors[status] || 'grey'
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-MX', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    approveContract(contract) {
      this.selectedContract = contract
      this.confirmAction = 'APPROVE'
      this.showConfirmDialog = true
    },
    rejectContract(contract) {
      this.selectedContract = contract
      this.confirmAction = 'REJECT'
      this.showConfirmDialog = true
    },
    submitApprove() {
      this.respondToContract('APPROVE')
    },
    submitReject() {
      this.respondToContract('REJECT')
    },
    respondToContract(action) {
      this.loadingContractId = this.selectedContract.id
      this.$store.dispatch(`security/${RESPOND_TO_PARK_CONTRACT}`, {
        token: this.getToken,
        contractId: this.selectedContract.id,
        action: action
      })
      .then(() => {
        this.showConfirmDialog = false
        const message = action === 'APPROVE' 
          ? this.$t('security_contract_approved')
          : this.$t('security_contract_rejected')
        this.$store.commit('snackbar_change_message', message)
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.fetchPendingContracts()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_responding_to_contract'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
      .finally(() => {
        this.loadingContractId = null
      })
    },
    fetchPendingContracts() {
      if (!this.parkId) {
        this.error = this.$t('error_park_not_found')
        console.warn('[PendingParkContracts] parkId not found. Profile:', this.$store.getters.getProfile)
        return
      }

      this.isLoading = true
      this.error = null
      console.log('[PendingParkContracts] Fetching contracts for parkId:', this.parkId)
      
      const config = {
        method: 'get',
        url: `${API_BASE_URL}/security/parks/${this.parkId}/contracts/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': `Token ${this.getToken}`
        }
      }
      
      axios(config)
        .then(resp => {
          console.log('[PendingParkContracts] Response:', resp.data)
          this.parkName = resp.data.park || ''
          this.totalContracts = resp.data.total_contracts || 0
          this.pendingContracts = resp.data.contracts || []
          this.isLoading = false
        })
        .catch(err => {
          this.isLoading = false
          console.error('[PendingParkContracts] Error:', err.response || err)
          const errorMessage = err.response && err.response.data && err.response.data.detail 
            ? err.response.data.detail
            : (err.response && err.response.status === 403 
              ? this.$t('error_permission_denied')
              : this.$t('error_fetching_contracts'))
          this.error = errorMessage
        })
    },
    getParques() {
      const config = {
        method: 'get',
        url: `${API_BASE_URL}/parques/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': `Token ${this.getToken}`
        }
      }
      return axios(config)
        .then(response => {
          console.log('[PendingParkContracts] /parques/ response:', response.data)
          // Manejar diferentes estructuras de respuesta
          let parques = response.data
          if (!Array.isArray(parques)) {
            // Si no es array, intentar extraer del objeto
            if (parques.results && Array.isArray(parques.results)) {
              parques = parques.results
            } else if (parques.parques && Array.isArray(parques.parques)) {
              parques = parques.parques
            } else {
              parques = [parques]
            }
          }
          
          if (parques && parques.length > 0) {
            this.parkId = parques[0].id
            this.parkName = parques[0].name || parques[0].nombre || 'Parque'
            console.log('[PendingParkContracts] Park set:', this.parkId, this.parkName)
          }
          return parques
        })
        .catch(error => {
          console.error('[PendingParkContracts] Error fetching /parques/:', error)
          throw error
        })
    },
    initWithProfile(profile) {
      console.log('[PendingParkContracts] Profile loaded:', profile)
      this.parkId = this.$route.params.parkId 
        || this.$route.query.parkId
        || (profile && (profile.id_park || profile.park_id || profile.park || profile.id_parque))
      
      if (this.parkId) {
        console.log('[PendingParkContracts] parkId from profile/route:', this.parkId)
        this.fetchPendingContracts()
      } else {
        console.log('[PendingParkContracts] parkId not found, fetching from /parques/')
        this.isLoading = true
        this.error = null
        this.getParques()
          .then(() => {
            console.log('[PendingParkContracts] After getParques, parkId:', this.parkId)
            if (this.parkId) {
              this.fetchPendingContracts()
            } else {
              this.isLoading = false
              this.error = this.$t('error_park_not_found')
              console.warn('[PendingParkContracts] No park found for this user')
            }
          })
          .catch((error) => {
            this.isLoading = false
            console.error('[PendingParkContracts] Failed to get parks:', error)
            const errorDetail = error.response && error.response.data && error.response.data.detail
              ? error.response.data.detail
              : this.$t('error_fetching_contracts')
            this.error = errorDetail
          })
      }
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }

    if (!this.$store.getters.isProfileLoaded) {
      this.$store.dispatch(USER_REQUEST)
        .then((profile) => {
          this.initWithProfile(profile || this.$store.getters.getProfile)
        })
        .catch(() => {
          this.$router.push('/')
        })
    } else {
      this.initWithProfile(this.$store.getters.getProfile)
    }
  }
}
</script>

<style scoped>
.mt-3 {
  margin-top: 20px;
}
</style>
